import React, { useState, useEffect } from 'react';
import './addNewCategory.css';

const AddNewCategory = () => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('https://car.amar.co.jp/api/master/car-catgories/get-all-categories', {
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      
      const result = await response.json();
      
      const mappedCategories = result.data.map(category => ({
        id: category.carCategoriesId,
        name: category.categoriesName,
        logoUrl: '',
        description: category.description
      }));
      
      setCategories(mappedCategories);
    } catch (err) {
      setError(err.message || 'Failed to fetch categories');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCategory({ ...newCategory, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newCategory.name) {
      setError('Category name is required');
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem('adminAuthToken');
      
      // Encode the category name and description for URL
      const encodedName = encodeURIComponent(newCategory.name);
      const encodedDescription = newCategory.description ? encodeURIComponent(newCategory.description) : '';
      
      // Build the URL with query parameters
      let apiUrl = `https://car.amar.co.jp/api/master/car-catgories/add/categories/${encodedName}`;
      if (encodedDescription) {
        apiUrl += `?description=${encodedDescription}`;
      }
      
      // Add new category
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          // 'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to add category');
      }
      
      await fetchCategories();
      resetForm();
    } catch (err) {
      setError(err.message || 'Failed to add category');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!newCategory.name || !editingId) {
      setError('Category name is required');
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem('adminAuthToken');
      
      // Encode the category name for URL
      const encodedName = encodeURIComponent(newCategory.name);
      
      // Build the URL for update
      const apiUrl = `https://car.amar.co.jp/api/master/car-catgories/update/categoriesName/${editingId}/${encodedName}`;
      
      // Update category
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to update category');
      }
      
      await fetchCategories();
      resetForm();
    } catch (err) {
      setError(err.message || 'Failed to update category');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setNewCategory({
      name: category.name,
      description: category.description || ''
    });
  };

  const resetForm = () => {
    setNewCategory({ name: '', description: '' });
    setEditingId(null);
    setError('');
  };

  const handleDeleteClick = (id) => {
    setCategoryToDelete(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      setIsLoading(true);
      const token = localStorage.getItem('adminAuthToken');
      
      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-catgories/delete/categories/${categoryToDelete}`,
        {
          method: 'DELETE',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete category');
      }
      
      // Refresh the categories list after successful deletion
      await fetchCategories();
    } catch (err) {
      setError(err.message || 'Failed to delete category');
    } finally {
      setIsLoading(false);
      setShowDeleteDialog(false);
      setCategoryToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setCategoryToDelete(null);
  };

  return (
    <div className="category-admin-container">
      <h2 className="admin-header">Category Management</h2>
      
      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="delete-dialog-overlay">
          <div className="delete-dialog">
            <p>Are you sure you want to delete this category?</p>
            <div className="dialog-buttons">
              <button onClick={confirmDelete} className="confirm-btn" disabled={isLoading}>
                {isLoading ? 'Deleting...' : 'Yes'}
              </button>
              <button onClick={cancelDelete} className="cancel-btn" disabled={isLoading}>
                No
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="admin-panel">
        <div className="form-section">
          <h3 className='h3-add-new-category'>{editingId ? 'Edit Category' : 'Add New Category'}</h3>
          <form onSubmit={editingId ? handleUpdate : handleSubmit}>
            <div className="form-group">
              <label>Category Name</label>
              <input
                type="text"
                name="name"
                value={newCategory.name}
                onChange={handleInputChange}
                placeholder="Enter category name"
                className="form-input"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Description (Optional)</label>
              <textarea
                name="description"
                value={newCategory.description}
                onChange={handleInputChange}
                placeholder="Enter category description (optional)"
                className="form-input"
                rows="3"
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={isLoading}>
                {isLoading ? 'Processing...' : (editingId ? 'Update' : 'Add')}
              </button>
              {editingId && (
                <button type="button" onClick={resetForm} className="cancel-btn">
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
        
        <div className="list-section">
          <h3 className='h3-add-new-category'>Existing Categories</h3>
          {isLoading && !categories.length ? (
            <div className="loading">Loading...</div>
          ) : (
            <div className="category-list">
              {categories.map(category => (
                <div key={category.id} className="category-item">
                  <div className="category-info">
                    <div className="category-logo">
                      {category.logoUrl && <img src={category.logoUrl} alt={category.name} />}
                    </div>
                    <div>
                      <div className="category-name">{category.name}</div>
                      {category.description && (
                        <div className="category-description">{category.description}</div>
                      )}
                    </div>
                  </div>
                  <div className="category-actions">
                    <button onClick={() => handleEdit(category)} className="edit-btn">
                      Edit
                    </button>
                    <button onClick={() => handleDeleteClick(category.id)} className="delete-btn">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddNewCategory;